import { Alert, isAlertHidden } from 'app/design/primitives/alert';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { differenceInDays, localeAwareFormat } from 'app/lib/date';
import { storage } from 'app/lib/storage';
import type { StorageKey } from 'app/lib/storage';
import { LoanFilterKey, useAllLoans } from 'app/network/loan';
import type { Loan } from 'app/types/loan';
import React, { type ComponentProps, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { FadeOut, LinearTransition } from 'react-native-reanimated';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useUISize } from 'app/utils/web-helpers';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { navigateToLoan } from 'app/screens/root-stack-helper';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconAlertTriangle, IconX } from '@tabler/icons-react-native';
const theme = createDSTheme();

const LARGE_UI_CARD_WIDTH = 416;
const ALERT_LAYOUT_TRANSITION = LinearTransition.duration(250);

// Find expired loans, and verify if they are hidden. Return visible expired loans.
export function useVisibleExpiredLoans() {
  const acceptedLoansQuery = useAllLoans(LoanFilterKey.Accepted);

  const expiredLoans = (acceptedLoansQuery.data ?? []).filter(
    (loan) => differenceInDays(new Date(), new Date(loan.end)) >= 1,
  );

  const [visibleExpiredLoanIds, setVisibleExpiredLoanIds] = useState<Set<Loan['id']>>(() => new Set());
  useEffect(() => {
    const currentExpiredLoans = (acceptedLoansQuery.data ?? []).filter(
      (loan) => differenceInDays(new Date(), new Date(loan.end)) >= 1,
    );
    if (!currentExpiredLoans.length) return;
    const checkIfAlertIsHidden = async (loan: Loan) => {
      if (!(await isAlertHidden(`late-loan-${loan.id}`))) {
        setVisibleExpiredLoanIds((loanIds) => new Set(loanIds).add(loan.id));
      }
    };
    currentExpiredLoans.forEach((loan) => void checkIfAlertIsHidden(loan));
  }, [acceptedLoansQuery.data]);

  const visibleExpiredLoans = expiredLoans.filter((loan) => visibleExpiredLoanIds.has(loan.id));

  const hideLoan = (loanId: string) => {
    setVisibleExpiredLoanIds((loanIds) => {
      const nextLoanIds = new Set(loanIds);
      nextLoanIds.delete(loanId);
      return nextLoanIds;
    });
  };

  return { visibleExpiredLoans, refetch: acceptedLoansQuery.refetch, hideLoan };
}

function AlertCard({
  end,
  id,
  lender,
  listing,
  onHide,
  tPrefix,
  width,
}: {
  end: string;
  id: string;
  lender: Loan['lender'];
  listing: Loan['listing'];
  onHide?: () => void;
  tPrefix: string;
  width?: number;
}) {
  const { t } = useTranslation();
  const navigation = useNavigation();

  return (
    <Pressable
      onPress={() => navigateToLoan(navigation, id, listing.id)}
      style={width ? { width, flex: 1 } : undefined}
      testID={`expired-loan-alert-${id}`}
    >
      <Alert
        backgroundColor={theme.colors['surface.error.subtle']}
        color={theme.colors['surface.error.default']}
        style={width ? { flex: 1 } : undefined}
        hideKey={`late-loan-${id}`}
        onHide={onHide}
        title={t(`${tPrefix}.title`, {
          days: differenceInDays(new Date(), new Date(end)),
          ns: 'common',
        })}
      >
        <Text variant="bodyBase">
          <Trans
            components={{ strong: <Text fontWeight="700" /> }}
            i18nKey={`${tPrefix}.firstParagraph`}
            ns="common"
            values={{
              date: localeAwareFormat(new Date(end), t('dateFormat_MMMMdYYYY', { ns: 'common' })),
              firstName: lender.profile?.firstName,
              item: listing.item.name,
              lastName: lender.profile?.lastName,
            }}
          />
        </Text>

        <Text marginTop="spacing.general.x4" variant="bodyBase">
          {t(`${tPrefix}.secondParagraph`, { ns: 'common' })}
        </Text>
      </Alert>
    </Pressable>
  );
}

function DesktopAlertCard({
  end,
  id,
  lender,
  listing,
  onHide,
  tPrefix,
}: {
  end: string;
  id: string;
  lender: Loan['lender'];
  listing: Loan['listing'];
  onHide: () => void;
  tPrefix: string;
}) {
  const { t } = useTranslation();
  const { t: tOneChannel } = useTranslation('one-channel');
  const navigation = useNavigation();

  const hideAlert = () => {
    void storage.saveString(`alert-late-loan-${id}` as StorageKey, JSON.stringify(true));
    onHide();
  };

  const buttonLabel =
    tPrefix === 'expiredService'
      ? tOneChannel('banner.manageService.borrower')
      : tPrefix === 'expiredSale'
        ? tOneChannel('banner.manageSale.borrower')
        : tPrefix === 'expiredDonation'
          ? tOneChannel('banner.manageDonation.borrower')
          : tOneChannel('banner.manageLoan.borrower');

  return (
    <Column style={styles.desktopCard}>
      <Pressable
        accessibilityLabel={t('close', { ns: 'accessibility' })}
        onPress={hideAlert}
        style={styles.closeButton}
      >
        <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.placeholder']} />
      </Pressable>

      <Column gap="spacing.general.x4" padding="spacing.general.x6">
        <Column gap="spacing.general.x2">
          <Row alignItems="center" gap="spacing.general.x3">
            <IconAlertTriangle
              strokeWidth={ICON_STROKE_WIDTH}
              size={32}
              color={theme.colors['surface.error.default']}
            />

            <Text color="surface.error.default" fontWeight="700" style={styles.desktopTitle}>
              {t(`${tPrefix}.title`, {
                days: differenceInDays(new Date(), new Date(end)),
                ns: 'common',
              })}
            </Text>
          </Row>

          <Text style={styles.desktopBody}>
            <Trans
              components={{ strong: <Text fontWeight="700" /> }}
              i18nKey={`${tPrefix}.firstParagraph`}
              ns="common"
              values={{
                date: localeAwareFormat(new Date(end), t('dateFormat_MMMMdYYYY', { ns: 'common' })),
                firstName: lender.profile?.firstName,
                item: listing.item.name,
                lastName: lender.profile?.lastName,
              }}
            />
          </Text>
        </Column>

        <Pressable
          onPress={() => navigateToLoan(navigation, id, listing.id)}
          style={styles.desktopSecondaryButton}
          testID={`expired-loan-alert-${id}`}
        >
          <Text fontWeight="600" style={styles.desktopSecondaryButtonText}>
            {buttonLabel}
          </Text>
        </Pressable>
      </Column>
    </Column>
  );
}

const ExpiredLoans = function ExpiredLoans({
  cardWidth = 320,
  horizontal = false,
  maxVisible,
  onHideLoan,
  visibleExpiredLoans: visibleExpiredLoansProp,
  ...props
}: ComponentProps<typeof Column> & {
  horizontal?: boolean;
  cardWidth?: number;
  maxVisible?: number;
  onHideLoan?: (loanId: string) => void;
  visibleExpiredLoans?: Loan[];
}) {
  const { isLargeUI } = useUISize();

  const { visibleExpiredLoans, hideLoan } = useVisibleExpiredLoans();
  const visibleLoans = visibleExpiredLoansProp ?? visibleExpiredLoans;
  const hideVisibleLoan = onHideLoan ?? hideLoan;

  const items = visibleLoans.map((loan) => {
    const isService = loan.listing.subtype === ListingSubtype.service;
    const isDonation = isDonationListing(loan.listing);
    const isSell = loan.listing.transactionMode === TransactionMode.sell;
    return {
      ...loan,
      tPrefix: isSell ? 'expiredSale' : isService ? 'expiredService' : isDonation ? 'expiredDonation' : 'expiredLoans',
    };
  });

  const visibleItems = maxVisible ? items.slice(0, maxVisible) : items;

  const windowHeight = useWindowDimensions().height;
  if (visibleLoans.length === 0) return null;

  if (isLargeUI) {
    return (
      <Column {...props} width={LARGE_UI_CARD_WIDTH}>
        <Column gap="spacing.general.x4" alignItems="stretch" style={{ maxHeight: windowHeight * 0.8 }}>
          {visibleItems.map(({ end, id, lender, listing, tPrefix }) => (
            <Animated.View key={id} exiting={FadeOut.duration(380)} layout={ALERT_LAYOUT_TRANSITION}>
              <DesktopAlertCard
                end={end}
                id={id}
                lender={lender}
                listing={listing}
                onHide={() => hideVisibleLoan(id)}
                tPrefix={tPrefix}
              />
            </Animated.View>
          ))}
        </Column>
      </Column>
    );
  }

  if (horizontal) {
    return (
      <Row gap="spacing.general.x4" {...props}>
        {visibleItems.map(({ end, id, lender, listing, tPrefix }) => (
          <Animated.View key={id} exiting={FadeOut.duration(380)} layout={ALERT_LAYOUT_TRANSITION}>
            <AlertCard
              end={end}
              id={id}
              lender={lender}
              listing={listing}
              onHide={() => hideVisibleLoan(id)}
              tPrefix={tPrefix}
              width={cardWidth}
            />
          </Animated.View>
        ))}
      </Row>
    );
  }

  return (
    <Column gap="spacing.general.x4" {...props}>
      {visibleItems.map(({ end, id, lender, listing, tPrefix }) => (
        <Animated.View key={id} exiting={FadeOut.duration(180)} layout={ALERT_LAYOUT_TRANSITION}>
          <AlertCard
            end={end}
            id={id}
            lender={lender}
            listing={listing}
            onHide={() => hideVisibleLoan(id)}
            tPrefix={tPrefix}
          />
        </Animated.View>
      ))}
    </Column>
  );
};

export { ExpiredLoans };

const styles = StyleSheet.create({
  closeButton: {
    position: 'absolute',
    right: 16,
    top: 18,
    zIndex: 1,
  },
  desktopBody: {
    color: theme.colors['text.primary'],
    fontSize: 16,
    lineHeight: 22,
  },
  desktopCard: {
    backgroundColor: theme.colors['surface.brand.secondary-4.subtle'],
    borderRadius: theme.borderRadii['radius.lg'],
    minHeight: 162,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)',
    width: LARGE_UI_CARD_WIDTH,
  },
  desktopScrollContent: {
    rowGap: theme.spacing['spacing.general.x4'],
  },
  desktopSecondaryButton: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: 'transparent',
    borderColor: theme.colors['text.primary'],
    borderRadius: 999,
    borderWidth: 1,
    height: 38,
    justifyContent: 'center',
    minWidth: 158,
    paddingHorizontal: theme.spacing['spacing.general.x6'],
  },
  desktopSecondaryButtonText: {
    color: theme.colors['text.primary'],
    fontSize: 16,
    lineHeight: 16,
  },
  desktopTitle: {
    flex: 1,
    fontSize: 18,
    lineHeight: 20,
  },
});
